// R17 — THE ONE ROSTER OF WHAT THIS SITE CAN REGISTER AS A GENERATION.
//
// `runGeneration(userId, kind, ...)` WRITES a kind. Two tables BRANCH on it: which room the work
// belongs to, and what a member is told the work IS. Both tables used to be hand-typed in
// server/index.js, both were written against the SAVED TYPE rather than the REGISTERED KIND, and
// nothing anywhere held the two together. Five generators drifted:
//
//     the table said            the code registered        so the room resolved to
//     "bubble-reading"          "bubble:<key>"             null
//     "human-design-compat…"    "hd-compatibility"         null
//     "chant"/"affirmation"     "craft:<kind>"             null
//     (nothing)                 "astrology-tradition:…"    null
//     (nothing)                 "person-birth-chart"       null
//
// A null room is SILENCE, three times over: the in-room queue strip filters `g.room === room` and
// drops it; the site-wide follower filters `g.room !== currentRoom` and `null !== null` is false, so
// it vanishes the moment she leaves the room it was started in; and "Open it" dispatches an
// undefined room, so the shelf drawer opens on the bare room list and the finished reading is never
// fetched. No error, no log, nothing red. She paid for it.
//
// So: ONE declaration, imported by the server (the primary reader) and reachable by a fixture that
// parses the REAL call sites out of the server and pushes every one of them through here. Imports
// nothing, so both sides and every gate can load it.
//
// THE ENTRY REQUIREMENT: a key is either an EXACT kind ("hd-compatibility") or a FAMILY — the part
// before the first colon of a namespaced kind ("bubble" for "bubble:love"). Exact wins over family,
// so a room that runs three different readings can name each one without losing the family default.

export const GENERATION_KINDS = {
  // ── Astrology ───────────────────────────────────────────────────────────────────────────────
  "birth-chart": { room: "astrology", label: "Your birth chart" },
  // A CIRCLE row's chart, not hers. The label says so, and it is deliberately not "Your".
  "person-birth-chart": { room: "astrology", label: "A birth chart" },
  "personality": { room: "astrology", label: "Your personality and purpose" },
  "compatibility": { room: "astrology", label: "Your compatibility" },
  "find-your-type": { room: "astrology", label: "Your type" },
  "mini-report": { room: "astrology", label: "Your reading" },
  "astrology-ask": { room: "astrology", label: "Your reading" },
  "prediction": { room: "astrology", label: "Your prediction" },
  "astrology-report": { room: "astrology", label: "Your astrology report" },
  // The Bubbles. Registered as "bubble:<key>", saved as type "bubble-reading" — which is the exact
  // mismatch this module exists to make impossible.
  "bubble": { room: "astrology", label: "Your reading" },
  // Vedic / Chinese / the other traditions. Registered as "astrology-tradition:<key>", saved as
  // type "astrology-tradition", and known to neither table until R17.
  "astrology-tradition": { room: "astrology", label: "Your reading" },

  // ── The Akashic Records (AKASHIC-1, her order 2026-09-01) ───────────────────────────────────
  // Registered ONE PER READING rather than as a family, for AC3's reason exactly: this room runs
  // five different readings and the queue strip has to NAME the one that is working. A member who
  // started Past-Life Connections must not be told "your soul record" while she waits.
  //
  // >>> **THESE FIVE LABELS ARE TYPED HERE AND ALSO LIVE IN `src/akashic.js`, AND THE CROSSING IS
  // GUARDED RATHER THAN HIDDEN.** <<<
  //
  // The obvious move was to import the roster and derive them. **R17-3j forbids it in as many
  // words** — *"the law imports nothing, so the server, the bundle and a .mjs gate can all load
  // it"* — and that clause is not weakened to make this round's change convenient, which is the
  // whole disease this file's own history is about.
  //
  // So the labels are duplicated ON PURPOSE, and `scripts/akashic-law.mjs` asserts the two sets are
  // character-for-character identical. **A guarded crossing is honest; a silent one is the R17
  // disease.** If they ever drift, the gate names which key and which spelling.
  // **THE FAMILY DEFAULT, AND IT IS NOT OPTIONAL.** The server registers `` `akashic:${key}` `` —
  // a namespaced kind whose suffix is decided at run time — so the FAMILY is what has to resolve or
  // a sixth reading added tomorrow falls straight back into the null room this whole module exists
  // to have abolished. R17 refused the push until this line existed, over exactly that expression.
  // The exact rows below still win for the five that exist, which is what lets the queue strip NAME
  // the reading that is working.
  "akashic": { room: "akashic", label: "Your reading" },
  "akashic:soul-record": { room: "akashic", label: "Your Soul Record" },
  "akashic:past-life-connections": { room: "akashic", label: "Past-Life Connections" },
  "akashic:soul-contracts": { room: "akashic", label: "Your Soul Contracts" },
  "akashic:past-life-gifts": { room: "akashic", label: "Past-Life Gifts and Talents" },
  "akashic:purpose-timeline": { room: "akashic", label: "Your Soul's Purpose Timeline" },

  // ── Numerology ──────────────────────────────────────────────────────────────────────────────
  "numerology": { room: "numerology", label: "Your numerology" },

  // ── Tarot ───────────────────────────────────────────────────────────────────────────────────
  "tarot": { room: "tarot", label: "Your cards" },

  // ── Human Design ────────────────────────────────────────────────────────────────────────────
  "human-design": { room: "human-design", label: "Your Human Design" },
  "hd-compatibility": { room: "human-design", label: "Your compatibility" },

  // ── Astrocartography ────────────────────────────────────────────────────────────────────────
  // AC3: this room runs three different readings and the queue strip has to NAME the one that is
  // working, so the exact kinds win over the family. A member who started a place reading is not
  // told "your map of the earth" while she waits.
  "astrocartography": { room: "astrocartography", label: "Your map of the earth" },
  "astrocartography:map": { room: "astrocartography", label: "Your map of the earth" },
  "astrocartography:location": { room: "astrocartography", label: "Your reading of a place" },
  "astrocartography:relocation": { room: "astrocartography", label: "Your reading of a move" },
  "astrocartography:check-place": { room: "astrocartography", label: "Your check of a place" },

  // ── Dreams / Palmistry ──────────────────────────────────────────────────────────────────────
  "dream": { room: "dream", label: "Your dream" },
  "palm-reading": { room: "palmistry", label: "Your palm reading" },

  // ── The Magic Room ──────────────────────────────────────────────────────────────────────────
  // The three craft generators register as "craft:<kind>"; each is named for what it is, because
  // "Your craft" is machinery and this table's whole job is to never show her that.
  // W12-2's nouns are the answer key for the rest of this block and the fixture IMPORTS `magicNoun`
  // to hold them to it, rather than re-typing her words a third time. This module stays
  // import-free; the crossing lives in the gate, which is where R13/R14/R15/R16 put it.
  // THE FAMILY ENTRY IS THE LOAD-BEARING ONE and the exact keys are refinements of it: a namespaced
  // kind gets its suffix from a catalog, so a fourth craft added tomorrow must still resolve rather
  // than fall to null. The fixture requires a family entry for every namespaced call site, and holds
  // these suffixes to the server's own CRAFT_KINDS.
  "craft": { room: "magic", label: "Your working" },
  "craft:chant": { room: "magic", label: "Your chant" },
  "craft:affirmation": { room: "magic", label: "Your affirmation" },
  "craft:incantation": { room: "magic", label: "Your incantation" },
  "petition": { room: "magic", label: "Your petition" },
  "keys-working": { room: "magic", label: "Your working" },
  "moses-working": { room: "magic", label: "Your working" },
  "herb-working": { room: "magic", label: "The herb" },
  "cauldron-working": { room: "magic", label: "Your working" },
  "oils-working": { room: "magic", label: "Your oil" },
  "spell": { room: "magic", label: "Your spell" },

  // ── Seals ───────────────────────────────────────────────────────────────────────────────────
  // Registered by the detached seal path, not by runGeneration. Same roster all the same.
  "seal": { room: "seals", label: "Your seal" },
};

// Exact key first, then the family. Returns the whole entry so a caller can take either field
// without asking this module the same question twice.
export function generationEntry(kind) {
  const raw = String(kind || "").trim();
  if (!raw) return null;
  return GENERATION_KINDS[raw] || GENERATION_KINDS[raw.split(":")[0]] || null;
}

// What a member is told something IS. Never the machinery: a kind like "mini-report:hidden-enemies"
// is our word, not theirs. An unrostered kind falls back to a READABLE form rather than leaking a
// raw key, and NEVER to a blank — a nameless indicator is worse than none. That fallback is the
// never-blank law, not coverage: scripts/r17-generation-roster.mjs holds every kind the code can
// actually register to an EXPLICIT entry above, so reaching this line is a fixture failure.
export function generationKindLabel(kind) {
  const raw = String(kind || "").trim();
  if (!raw) return "Your reading";
  const entry = generationEntry(raw);
  if (entry) return entry.label;
  return `Your ${raw.split(":")[0].replace(/[-_]+/g, " ")}`;
}

// Which ROOM a kind's work belongs to, so the in-room strip shows this room's work and the follower
// can hand a finished reading straight over. `null` means "no room", and the callers treat that as
// silence — which is why nothing that can be registered may resolve to it.
export function generationKindRoom(kind) {
  return generationEntry(kind)?.room || null;
}

// Every room this roster can emit. The crossing the fixture holds: the shelf drawer must be able to
// OPEN each one of these, or "Open it" lands on a room the client does not list.
export function generationRooms() {
  return [...new Set(Object.values(GENERATION_KINDS).map((e) => e.room))].sort();
}

// R217 — **DOES THIS ROOM HAVE WORK IN FLIGHT?** IT IS A ROOM'S QUESTION AND IT WAS DECLARED INSIDE
// ONE ROOM'S LAW, SO THE SECOND ROOM THAT NEEDED IT COULD NOT ASK.
//
// `acgRunState` below answers two things, and only one of them is about astrocartography: `running`
// is per-MODE and is that room's own (AC3), while `any` is *this room has a generation going* — the
// same sentence every room on this site could ask and exactly one of them did. `roomGenerations` is
// that sentence, declared once, so `acgRunState`'s `any` and `sealRunState`'s `working` cannot be two
// answers to it (R28/R29).
//
// The comparison is the ROOM the server resolved (`generationRoom(kind)`, put on the payload by
// `handleActiveGenerations`), never the label and never the kind: a label is copy (R19's own defect)
// and a kind is narrower than the question — a row whose kind this roster cannot name is still WORK,
// and a room that ignored it would open a paid door over a run it could not name (R19's silent
// direction). An unnamed room matches nothing, which is the caller's bug rather than a state.
//
// **THE COMPARISON IS EXACT AND THAT IS THE WHOLE GUARD.** `"seals"` and `"seal"` are one character
// apart and both are real strings in this house (the ROOM and the KIND), so anything looser than
// equality makes one room answer for another. The first draft carried an `if (!want) return []` above
// this line and the mutation run reported it a behavioural NO-OP, correctly: no row this site
// produces carries `room: ""` (`generationRoom` answers a name or `null`), so nothing could reach it.
// **It was removed from the LAW rather than the mutation retargeted around it** — R216's own ruling:
// a line that cannot change an answer is a line no mutation can reach and no reader can trust. An
// unnamed room still matches nothing, because equality says so.
export function roomGenerations(room, active) {
  const want = String(room || "");
  return (Array.isArray(active) ? active : []).filter((g) => g && g.room === want);
}

export function roomWorkInFlight(room, active) {
  return roomGenerations(room, active).length > 0;
}

// R19 — THE ASTROCARTOGRAPHY ROOM ASKS THE SERVER "IS THIS ONE OF MINE ALREADY RUNNING?", because
// local busy state dies on navigation and a member who wanders off mid-run must come back to a
// button that is still working. It used to ask by RUNNING A REGEX OVER THE MEMBER-FACING LABEL
// (/reading of a place/i against "Your reading of a place"), so the two sides were coupled by PROSE:
// a copy edit — the one kind of change nobody thinks of as a code change — would quietly make a
// running job look idle, and she would press Generate on a paid call already in flight. It asks by
// KIND now, and this is where the kind is built. Held to the route's own guard list by the fixture.
export const ACG_FAMILY = "astrocartography";
export const ACG_MODES = ["map", "location", "relocation", "check-place"];
export function acgKind(mode) {
  return `${ACG_FAMILY}:${String(mode || "").trim()}`;
}

// R215 — *IS THIS READING RUNNING* AND *MAY THIS DOOR BE PRESSED* ARE TWO QUESTIONS (R18), AND ONLY
// THE FIRST ONE EVER ASKED THE SERVER.
//
// R19 above is why the first one is server-side: local `busy` dies on navigation, so a member who
// wanders off mid-run must come back to a button that is still working. That worked. What nobody
// wired to the same answer is the DISABLE — the room's two place buttons were `disabled={busy}`,
// local only, so on the return R19 exists for the button PULSED "Reading that place…" **and was
// fully pressable**, and the tap bought a SECOND paid generation of a reading already in flight.
// R19's own defect (*"she presses Generate on a PAID engine call that is already going"*) arriving
// through the disable instead of through the label, under a comment that says the room asks the
// server. The map submit had the opposite half of the same hole: it disabled on `mapRunning` alone,
// so it stayed live all through a location or relocation run — two paid casts, and one `status`,
// `report` and `busy` between them.
//
// So the two answers come off ONE walk of ONE roster and cannot disagree (R28/R29):
//   `running[mode]` — is THAT reading going, locally or on the server. It is what a door PULSES on,
//     and it is per mode, because "not 'your map' to someone waiting on a place" is AC3's own rule.
//   `any` — is ANY of this room's readings going. It is what every door DISABLES on, because the
//     three share one `busy`, one `status` and one `report`, so a second run started over the first
//     scrambles all three whichever door starts it.
//
// AND THE TWO ANSWERS TAKE OPPOSITE SAFE DIRECTIONS ON AN UNREADABLE ROW, WHICH IS THE HALF THAT IS
// EASY TO GET WRONG BY MAKING THEM AGREE. `running[m]` demands an EXACT kind, because a pulse that
// over-matches says "your map is going" to a member waiting on a place — the thing AC3 forbids by
// name. `any` asks only whether this room has WORK IN FLIGHT, so a row here whose kind this roster
// cannot name still closes every door: over-disabling costs her a wait she can see, under-disabling
// costs her a paid generation she cannot (R19's direction, R8-4's silent one).
export function acgRunState({ mode, busy, active } = {}) {
  const kinds = new Set(roomGenerations(ACG_FAMILY, active).map((g) => (typeof g.kind === "string" ? g.kind : "")));
  const local = Boolean(busy);
  const running = {};
  for (const m of ACG_MODES) running[m] = (local && mode === m) || kinds.has(acgKind(m));
  // R217: `any` was `mine.length > 0` written here. It is the shared sentence now, unchanged in
  // meaning — the fixture drives both halves over the same rosters and holds them equal.
  return { running, any: local || roomWorkInFlight(ACG_FAMILY, active) };
}

// R217 — **THE POLL GAVE UP AND THE SEAL DID NOT, AND THE ROOM SAID ONE SENTENCE FOR BOTH.**
//
// The Seal room is the OTHER of the only two rooms on this site that render the in-room queue strip,
// and its own comment at that strip names the case in as many words — *"they returned mid-run, or
// started one and wandered"*. It asked the server nothing. Its Generate button's LABEL and its
// DISABLE both read a local `busy` that dies on navigation, so R19's defect (*the button reads idle
// while the run is in flight*) and R215's (*the disable never got wired to the answer the room had
// already bought*) were both live here, on **the most expensive single generation in this house** —
// S2's Gemini render plus a different-vendor vision truth gate plus up to three regenerations.
//
// **AND THE SERVER'S OWN DEDUPE CANNOT COVER THE CASE IT LOOKS LIKE IT COVERS.** `handleSealGenerate`
// keys a duplicate on the CONTENT of the request (`genKeyFor(user, "seal", JSON.stringify(...))`), so
// it folds a double-tap on an untouched form perfectly — and the two states where `busy` is false
// with a seal still drawing are exactly the two where the content is not untouched: she came back to
// an EMPTY form and retyped, or the five-minute poll gave up and she adjusted a word before pressing
// again. One character apart is a different key and a second paid seal.
//
// TWO ANSWERS, AND THE SECOND ONE IS THE ROUND (R18):
//   `working`   — is a seal of hers being drawn, locally or on the server. ONE door here, so one
//                 answer: it is what the door PULSES on and what it CLOSES on, unlike AC3's room
//                 where the pulse is per mode. Two identical fields would be a line no mutation can
//                 reach and no reader can trust (R216's own ruling), so there is one.
//   `resumable` — **THE POLL GAVE UP AND THE SEAL DID NOT.** `pollSeal` returns `retryable: true`
//                 with *"taking longer than usual"* at its five-minute deadline while the detached
//                 pipeline draws on, and the room answered that with the same **Run it again** it
//                 offers a seal that genuinely died. Those are two facts. The tap she needs here is
//                 not a new seal, it is the wait resumed — and it costs nothing.
//
// `jobKey` is READ OFF THE POLL PAYLOAD RATHER THAN REMEMBERED, and that is what makes the resume
// survive a navigation: `/api/generations/active` sends `id: g.genKey`, and a seal's `genKey` IS the
// `jobKey` `/api/seals/status` is polled by — the same string, already on the wire (R28/R29: no
// second carrier for a value the house already sends). Oldest first is the server's own ordering:
// the thing she has been waiting on longest is the thing she means. **No readable key means no
// resume**, so the room falls back to exactly today's control rather than to a tap that cannot work.
export const SEAL_ROOM = "seals";

export function sealRunState({ busy, active } = {}) {
  const mine = roomGenerations(SEAL_ROOM, active);
  const local = Boolean(busy);
  const first = mine.find((g) => typeof g.id === "string" && g.id);
  const jobKey = first ? first.id : "";
  return { working: local || mine.length > 0, resumable: !local && Boolean(jobKey), jobKey };
}

// R220 — **THE ASTROLOGY ROOM'S TWO DOORS ASKED THIS BROWSER WHETHER THEY WERE RUNNING, AND THE
// DEDUPE THAT LOOKS LIKE IT COVERS THEM CANNOT COVER THE ONE CASE THEY FAIL IN.**
//
// R19 built the server-side answer because local `busy` dies on navigation; R215 wired the DISABLE
// to it one room over; R217 found both halves live in the Seal room. This is the third room and the
// FOURTH time: `AstrologyPage`'s two generator doors — Find Your Type and *Want to know more?* —
// read a purely local `typeBusy` / `askBusy` for their LABEL, their pulse class AND their `disabled`.
// Both register with the server through `runGeneration`, both resolve to room `astrology`, and both
// carry their exact kind on `/api/generations/active`. The room asked none of it.
//
// **AND R218 IS WHAT MAKES THE CONTRADICTION VISIBLE RATHER THAN MERELY TRUE.** `astrology` is not in
// `QUEUE_STRIP_ROOMS`, so before that round this room said NOTHING while work was in flight and there
// was nothing to disagree with. The dock says it now — so on the return the dock reads *"Your type ·
// still working"* while the button one screen away reads **Find My Type** and is fully pressable.
// R217's finding with the surfaces swapped (a dock rather than a strip), word for word.
//
// **THE ASK DOOR IS A HARDER CASE THAN THE SEAL'S, AND THAT IS THE ROUND.** `handleAstrologyAsk` keys
// its dedupe on `${chartIdentity(chart)}|${question.toLowerCase()}` — so it folds a double-tap on an
// untouched form perfectly, and **her question box is EMPTY after a navigation by construction**
// (`askQuestion` is local state). So the ONLY way back to that door is retyping a free-text sentence,
// and a sentence retyped identically is the coincidence rather than the rule. She comes back, sees an
// idle button and no status, believes it never went through, retypes *"will I get the job"* without
// the question mark — and that is a SECOND PAID GENERATION of the reading she is already waiting for.
// The seal room needed her to edit a form; this one guarantees the edit.
//
// **ONE ANSWER PER DOOR, KEYED ON THE EXACT KIND** — AC3's rule, and it binds harder here than in
// either earlier room. Room `astrology` is the busiest kind-space in this roster (`birth-chart`,
// `personality`, `compatibility`, `mini-report`, `prediction`, `bubble`, `astrology-tradition`,
// `astrology-report` and both of these), so a door that asked *does this ROOM have work* would pulse
// **"Finding your type…"** at a member waiting on a prediction — the exact thing AC3 forbids by name.
// The kind is the reading's identity and is globally unique in R17's roster above; the room is a
// coarser fact derived from it, and this door is not asking a room question (R41).
//
// **>>> AND R215's `any`-CLOSES-EVERY-DOOR RULING IS DELIBERATELY NOT COPIED, BECAUSE ITS REASON DOES
// NOT HOLD HERE. <<<** That room disables every door on `any` for a stated cause — its three readings
// *"share one `busy`, one `status` and one `report`, so a second run started over the first scrambles
// all three whichever door starts it."* **These two doors share NOTHING**: `typeBusy`/`typeStatus`/
// `typeReport` and `askBusy`/`askStatus`/`askReport` are separate state, and a member is entitled to
// ask a question while her type reading builds. Carrying that ruling across would cost her a
// legitimate action for no defect — a change with no defect under it (R19). So each door answers for
// itself, and the difference is written down rather than inherited.
//
// **ONE FIELD PER DOOR, BECAUSE THE PULSE AND THE CLOSE ARE THE SAME ANSWER HERE.** Unlike AC3's room
// there is no per-mode pulse to keep apart from a room-wide close, and two identical fields would be
// a line no mutation can reach and no reader can trust (R216's own ruling, applied rather than
// re-derived). The local flag is ORed in for the same reason R215 keeps it: the poll is every three
// seconds, so it is what covers the door between the tap and the server's first answer.
export const ASTRO_ROOM = "astrology";
export const ASTRO_DOORS = { type: "find-your-type", ask: "astrology-ask" };
export const ASTRO_DOOR_KEYS = Object.keys(ASTRO_DOORS);

export function astroRunState({ busy, active } = {}) {
  const kinds = new Set(
    (Array.isArray(active) ? active : []).map((g) => (g && typeof g.kind === "string" ? g.kind : "")),
  );
  const running = {};
  for (const d of ASTRO_DOOR_KEYS) running[d] = Boolean(busy && busy[d]) || kinds.has(ASTRO_DOORS[d]);
  return { running };
}

// ---------------------------------------------------------------------------------------------
// R222 — **THE FOUR MODAL DOORS, WHICH ARE THE LAST LOCAL-FLAG GENERATORS IN THIS HOUSE.**
//
// R220's own closing line named them and declared them OUT OF SCOPE in as many words:
// *"THE FOUR MODALS THIS ROOM OPENS ARE OUT OF SCOPE AND SAID SO — `AstrologyReadingModal`,
// `MiniReportModal`, `PredictionModal` and `DeepReportModal` are separate components with their own
// state, they were NOT audited for this defect."* This is that audit, and the defect is there: each
// one reads a purely local `busy` for its label and its `disabled`, and **a local flag dies on
// navigation** — so a member who starts a reading, leaves the modal and comes back meets an idle
// button on a paid generation that is already running, and a second tap buys it twice.
//
// **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180): ALL THREE GENERATING DOORS REALLY REGISTER.**
// `handleMiniReport`, `handlePrediction` and `handleAstrologyAnalysis` each go through
// `runGeneration`, so `/api/generations/active` carries them and there is something to ask. A door
// that registered nothing would be a different item, and the fixture asserts the roster is real
// rather than assuming it.
//
// >>> AND ONE OF THE THREE IS NAMESPACED, WHICH IS THIS LAW'S ONE NEW CASE. <<< The deep report
// registers `astrology-report:${config.key}` — one kind per reading it can build — so an EXACT
// match would answer false for every one of them while reading perfectly. R217 met the same shape on
// the seal room (`seal:something-new`, whose FAMILY is `seal`) and the answer is the same: a door
// declares whether its kind is exact or a FAMILY, and a family matches on the segment before the
// colon rather than on a prefix, because a prefix test would let `astrology-report-draft` in.
export const MODAL_DOORS = Object.freeze({
  // >>> R223 — mini AND prediction ARE FAMILIES TOO, AND DECLARING THEM EXACT MADE TWO OF R222'S
  // THREE DOORS DEAD ON THE DAY THEY SHIPPED. <<< Measured against the server: `handleMiniReport`
  // registers `mini-report:${config.key}` and `handlePrediction` registers
  // `prediction:${config.key}` at BOTH of its call sites, and `runGeneration` stores the kind RAW
  // (`inFlight.set(genKey, { userId, kind, ... })`), so `/api/generations/active` carries
  // `mini-report:hidden-enemies` and never the bare word. An EXACT match therefore answered FALSE
  // for every reading either door can build — the repair present, reading perfectly, firing on
  // nothing, which is the class R222 was written to close arriving inside R222.
  mini: { kind: "mini-report", family: true },
  prediction: { kind: "prediction", family: true },
  deep: { kind: "astrology-report", family: true },
  // >>> R224 — THE FOURTH DOOR, AND R222'S ROSTER NEVER HAD IT. <<< `handleReadingBubble`
  // registers `bubble:${bubble.key}` — a FAMILY, exactly like the three above it — so the
  // server has always known when one of hers is in flight and `ReadingBubbleModal` was reading
  // a purely LOCAL flag that dies on navigation. It is the same defect R222 was written to
  // close, on a door that round did not measure: found by deriving the roster from the SERVER
  // (every handler that calls `runGeneration`) rather than from the hand-typed route list
  // R223 used, which is what made R223's own census under-count.
  bubble: { kind: "bubble", family: true },
});
export const MODAL_DOOR_KEYS = Object.freeze(Object.keys(MODAL_DOORS));

// One field per door, for `astroRunState`'s own recorded reason: there is no per-mode pulse to keep
// apart from a room-wide close here, and two identical fields would be a line no mutation can reach
// and no reader can trust (R216). The local flag is ORed in because the poll is every three seconds,
// so it is what covers the door between her tap and the server's first answer (R215).
//
// **AND EACH DOOR ANSWERS FOR ITSELF RATHER THAN FOR THE ROOM**, which is R220's ruling: room
// `astrology` is the busiest kind-space in R17's roster, so a door asking *does this ROOM have work*
// would pulse "Building your prediction..." at a member waiting on a mini report.
export function modalRunState({ busy, active } = {}) {
  return doorRunState(MODAL_DOORS, { busy, active });
}

// ---------------------------------------------------------------------------------------------
// R223 — **THE ONE READER OF *IS THIS DOOR'S KIND IN FLIGHT*, BECAUSE THE THIRD SPELLING IS WHAT
// DRIFTED.**
//
// This body was written once inside `modalRunState` and was about to be written a second time for
// the photo doors below. It is lifted instead, and `modalRunState` delegates — two spellings of
// *does this kind match this door* is two answer keys (R28/R29), **and the one that drifted is
// exactly what R223 found**: a family declared exact, matching a kind the server never writes.
//
// A FAMILY matches the segment before the first colon and never a bare prefix, so
// `astrology-report-draft` is a different kind and stays out (R222's own case, kept).
// The local flag is ORed in for R215's reason: the poll is every three seconds, so it is what
// covers the door between her tap and the server's first answer.
//
// **`astroRunState` ABOVE IS DELIBERATELY NOT FOLDED IN, AND THAT IS A MEASUREMENT RATHER THAN AN
// OVERSIGHT (R19, scope rule 4).** Both of its doors register EXACT kinds — `runGeneration(userId,
// "find-your-type", ...)` and `runGeneration(userId, "astrology-ask", ...)`, verified against the
// server — so it has no defect under it, and re-shaping its roster to fit this reader would be a
// change nobody measured plus a migration on R220's fixture.
export function doorRunState(doors, { busy, active } = {}) {
  const kinds = (Array.isArray(active) ? active : [])
    .map((g) => (g && typeof g.kind === "string" ? g.kind : ""))
    .filter(Boolean);
  const running = {};
  for (const d of Object.keys(doors || {})) {
    const { kind, family } = doors[d];
    const matched = family
      ? kinds.some((k) => k === kind || k.slice(0, kind.length + 1) === `${kind}:`)
      : kinds.includes(kind);
    running[d] = Boolean(busy && busy[d]) || matched;
  }
  return { running };
}

// ---------------------------------------------------------------------------------------------
// R223 — **THE PHOTO DOOR, WHERE THE DEDUPE STRUCTURALLY CANNOT SAVE HER.**
//
// R19, R215, R217, R220 and R222 each repaired one room of this class: a generator door whose label
// and `disabled` read a purely LOCAL `busy`, which dies on navigation, so a member who starts a
// paid reading and comes back meets an idle button on work already running.
//
// **WHAT PICKS THIS DOOR OUT OF THE SEVEN THAT ARE LEFT IS A MEASUREMENT AND NEVER A CHOICE: THE
// DEDUPE KEY.** `runGeneration`'s `inputKey` folds a resubmit into the running job at every other
// local-flag door on this site, because every one of them keys on stable content — a chart
// identity, a lowercased name, the situation text she typed. **Palmistry keys on
// `palm-${palm.id}`, and `savePalmImage` INSERTS A ROW PER UPLOAD** — so the photo she re-picks
// on her return is a new row, a new id, a new key, and the dedupe cannot fold it. Not *one
// character apart* (R217's seal room) but **ALWAYS different, by construction.**
//
// AND IT IS THE MOST EXPENSIVE DOOR IN THE HOUSE TO GET WRONG, ON THIS HOUSE'S OWN NUMBERS:
// IT #145 measured a DELIVERED palm reading at a **median 87.8 seconds**, with 200 of its 208
// taking over a minute, and then raised the per-call ceiling to 110s with up to three attempts —
// so the window in which this door reads idle while a paid VISION call is running is longer now
// than it has ever been. A ninety-second wait is the one a member walks away from.
//
// >>> THE TAROT ANALYZER IS THE OTHER PHOTO DOOR AND IS DECLARED OUT WITH ITS MEASUREMENT
// (R17/R160), NOT OVERLOOKED. <<< It carries the identical shape — `spread-photo-${photo.id}-...`,
// and its own call site says so in as many words (*"keyed on the photo id, which is unique per
// upload, so a re-send is a new read"*) — but it registers kind `"tarot"`, **and so does the
// ordinary card draw**, while `genKeyFor` hashes the inputKey away and `generationLabel("tarot")`
// answers one string for both. So no discriminator reaches the client today: a door keyed on
// `tarot` would shut the analyze button and pulse *reading your spread* at a member who is waiting
// on a card pull, on the SAME PAGE (`TarotAnalyzePanel` is mounted inside `TarotPage`) — which is
// R220's ruling broken by the round that quotes it. Giving that analyze its own kind changes R17's
// roster and the label a member reads while she waits, which is member-visible and is its own item.
export const PHOTO_DOORS = Object.freeze({
  palm: { kind: "palm-reading", family: false },
  // >>> R225 — THE OTHER PHOTO DOOR, ONCE ITS KIND COULD BE NAMED. <<< EXACT and never a family:
  // the bare `tarot` is the card DRAW, and shutting the analyze button while a member waits on a
  // card pull is R220's ruling broken on the SAME PAGE, since `TarotAnalyzePanel` is mounted
  // inside `TarotPage`. `tarot:analyze` is the analyze and nothing else.
  tarot: { kind: "tarot:analyze", family: false },
});
export const PHOTO_DOOR_KEYS = Object.freeze(Object.keys(PHOTO_DOORS));

export function photoRunState({ busy, active } = {}) {
  return doorRunState(PHOTO_DOORS, { busy, active });
}
